import { createModel } from '@rematch/core';
import { SkuType } from 'domains/skuDomain';
import gallerySkuService from 'services/gallerySkuService';
import { GallerySkuFilter } from 'utils/gallerySkuFilter';
import type { RootModel } from '.';
import {
  GALLERY_RENDER_ENUM,
  GallerySku,
  SkuGalleryLabelCount,
} from '../domains/gallerySkuDomain';
import { BaseState, baseReducers } from './baseReducers';

interface CompanyGalleryState extends BaseState {
  skuTypes: GallerySku[];
  selectedSkuType: SkuType | null;
  galleryRenderType: GALLERY_RENDER_ENUM | GALLERY_RENDER_ENUM.DEFAULT;
  /**
   * Company labels for this gallery visit, with skuCount scoped to the
   * ?skuIds= / ?skuTags= filter. Kept apart from the shared gallery.companyLabels
   * so neither the filter nor the SSO-buyer label set leaks into the header shop
   * menu - and so a Canva SSO buyer, whose shared companyLabels come back empty,
   * still gets the categories on this page. Null until the first load lands.
   */
  pageCompanyLabels: SkuGalleryLabelCount[] | null;
}

export const companyGallery = createModel<RootModel>()({
  state: {
    skuTypes: [],
    selectedSkuType: null,
    galleryRenderType: GALLERY_RENDER_ENUM.DEFAULT,
    pageCompanyLabels: null,
  } as unknown as CompanyGalleryState,
  reducers: {
    ...baseReducers<CompanyGalleryState>(),
  },
  effects: (dispatch) => {
    const { companyGallery } = dispatch;
    return {
      async getSkuTypes(): Promise<GallerySku[]> {
        const { content } = await gallerySkuService.getCompanySkusByLabel({
          companyLabelId: -1,
          size: 10000,
        });
        companyGallery.setState({
          skuTypes: content,
        });
        return content;
      },
      /**
       * Loads the categories this page shows, with counts scoped to the
       * ?skuIds= / ?skuTags= filter when the route carries one.
       */
      async loadPageCompanyLabels(payload: GallerySkuFilter) {
        const labels = await gallerySkuService.getGalleryLabels(payload);
        companyGallery.setState({
          pageCompanyLabels: labels.companyLabels || [],
        });
      },
      setSelectedSkuType(payload: SkuType | null): void {
        dispatch.companyGallery.setState({ selectedSkuType: payload });
      },
      setGalleryRenderType(payload: GALLERY_RENDER_ENUM): void {
        companyGallery.setState({
          galleryRenderType: payload,
        });
      },
    };
  },
});
